import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { API_BASE_URL } from '../../utils/apiConfig';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 32px 48px;
  background: #fff;
  font-family: Georgia, 'Times New Roman', Times, serif;
  color: #111;

  @media (max-width: 768px) {
    margin: 16px auto;
    padding: 28px 20px;
    max-width: 96%;
  }
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 12px auto;
  width: 100%;
  max-width: 780px;
`;

const HeaderBlock = styled.div`
  text-align: center;
  margin-bottom: 40px;
  font-size: 0.95rem;
  line-height: 1.45;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 22px;
  font-size: 1.05rem;
`;

const FieldLabel = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
  font-weight: 400;
`;

const FieldValue = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  border-bottom: 1px solid #222;
  padding: 0 6px 2px;
  min-height: 1.35em;
  word-break: break-word;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 40px auto 0 auto;
  display: block;
  font-family: sans-serif;

  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  margin: 40px 0;
  font-family: sans-serif;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
  font-family: sans-serif;
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }

    @page {
      margin: 0.6in;
      size: letter;
    }

    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      background: #fff;
    }
  }
`;

function formatDateDisplay(value) {
  if (!value) return '';
  const raw = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return String(value);
  const [year, month, day] = raw.split('-');
  return `${month}/${day}/${year}`;
}

function displayValue(value) {
  if (value == null || String(value).trim() === '') return '';
  return String(value);
}

const StudentEmploymentForm = () => {
  const { id } = useParams();
  const docRef = useRef(null);
  const [employment, setEmployment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/students/${id}/employment`);
        const result = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok || !result?.success || !result.data) {
          setError(result?.message || 'Employment information not found');
          setEmployment(null);
          return;
        }
        setEmployment(result.data);
        const name = result.data.studentName || 'Student';
        document.title = `${name}-${id}-Student-Employment-Form`;
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Error loading employment information');
        setEmployment(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) {
      load();
    } else {
      setLoading(false);
      setError('Student id is required');
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading employment information...</LoadingMessage>
      </Container>
    );
  }

  if (error || !employment) {
    return (
      <Container>
        <ErrorMessage>{error || 'Employment information not found'}</ErrorMessage>
      </Container>
    );
  }

  const fields = [
    { label: 'Student Name:', value: displayValue(employment.studentName) },
    { label: 'Last 4 of S. S. Number:', value: displayValue(employment.lastFourSsn) },
    { label: 'Date of Birth:', value: formatDateDisplay(employment.dateOfBirth) },
    { label: 'Email Address:', value: displayValue(employment.email) },
    { label: 'Workforce:', value: displayValue(employment.workforce) },
    { label: 'Place of Employment:', value: displayValue(employment.placeOfEmployment) },
    { label: 'Position:', value: displayValue(employment.position) },
    { label: 'Full or Part Time:', value: displayValue(employment.employmentType) },
    {
      label: 'Hourly Rate:',
      value: employment.hourlyRate != null ? String(employment.hourlyRate) : ''
    },
    {
      label: 'Average Hours per week:',
      value: employment.averageHoursPerWeek != null ? String(employment.averageHoursPerWeek) : ''
    },
    { label: 'Date Hired:', value: formatDateDisplay(employment.dateHired) }
  ];

  return (
    <Container ref={docRef}>
      <PrintHideStyle />
      <Logo src={`${process.env.PUBLIC_URL}/forms_logo.png`} alt="CCLC Logo" />
      <HeaderBlock>
        <div>840 W. Irving Park Rd, suite 203 Chicago, Illinois 60613</div>
        <div>(773) 506-1503 office</div>
        <div>
          <a href="https://www.cclctraining.org" target="_blank" rel="noopener noreferrer">
            www.cclctraining.org
          </a>
        </div>
      </HeaderBlock>

      {fields.map((field) => (
        <FieldRow key={field.label}>
          <FieldLabel>{field.label}</FieldLabel>
          <FieldValue>{field.value}</FieldValue>
        </FieldRow>
      ))}

      <DownloadButton type="button" className="print-hide" onClick={handlePrint}>
        Print / Download
      </DownloadButton>
    </Container>
  );
};

export default StudentEmploymentForm;
